// Синхронизация: стрим событий → локальная база (ADR-010, §5.8 контракта).
//
// Три правила, которые здесь и живут:
//   • курсор сохраняется ПОСЛЕ применения события, иначе разрыв между «сохранили» и «применили»
//     теряет событие навсегда;
//   • события без курсора (пульс, resync) курсор не двигают;
//   • `resync_required` — не ошибка, а указание перезалить: локальная база стирается и собирается
//     заново, потому что догонять больше не от чего.
//
// События применяются порциями: всплеск в сотню сообщений не должен занимать поток дольше кадра
// (§1.3 архитектуры, планка §5).

import { createClient, type Transport } from "@connectrpc/connect";
import { MemberChange, EventService } from "@yofis/proto/yofis/v1/event_pb";
import type { Event } from "@yofis/proto/yofis/v1/event_pb";
import { ChatService } from "@yofis/proto/yofis/v1/chat_pb";
import { MessageService } from "@yofis/proto/yofis/v1/message_pb";
import type { Message as ProtoMessage } from "@yofis/proto/yofis/v1/message_pb";
import type { Chat as ProtoChat } from "@yofis/proto/yofis/v1/chat_pb";

import type { Clock } from "./platform.ts";
import { systemClock } from "./platform.ts";
import type { LocalChat, LocalMessage, LocalStore } from "./store.ts";

/** Что синхронизация сообщает наверх. */
export interface SyncListener {
  /** Локальные данные изменились: UI пора перечитать. */
  changed?(): void;
  /** Стрим оборвался и будет переоткрыт через указанное время. */
  disconnected?(error: unknown, retryInMs: number): void;
  /** Сервер потребовал полную перезагрузку. */
  resynced?(): void;
  /**
   * Кто-то просится в чат или встречу через лобби (или получил решение по своей заявке).
   *
   * Базу это не меняет — заявка живёт на узле, — но показать её обязан тот, кто впускает: заявка,
   * которую никто не увидел, для просящегося выглядит как молчание.
   */
  lobby?(targetId: string, address: string, displayName: string, state: number): void;
  /**
   * Вход, на который стоит посмотреть (0.16): незнакомое устройство или вход по ссылке.
   *
   * Событие несёт устройство целиком именно затем, чтобы клиент довёл до отзыва. Уведомление без
   * кнопки «это не я» хуже молчания: человек узнаёт, что кто-то вошёл, и не может ничего сделать.
   */
  signIn?(deviceId: string, deviceName: string, method: number, atMillis: number): void;
  /**
   * Кто-то печатает. В базу не пишется: состояние живёт секунды и гаснет само.
   *
   * `untilMillis` — до какого момента считать, что печатает: гашение обязано быть по сроку, а не по
   * приходу «перестал». Событие «перестал» может не дойти вовсе (человек закрыл вкладку), и
   * индикатор, который ждёт его, остаётся навсегда — это классика.
   */
  typing?(chatId: string, address: string, untilMillis: number): void;
  /**
   * Кто сейчас в сети. В базу не пишется: «в сети», пережившее перезапуск, — это ложь о человеке,
   * который давно ушёл. Состояние живое и приходит заново при каждом подключении.
   */
  presence?(address: string, state: number, lastSeenMillis: number): void;
}

export interface SyncOptions {
  transport: Transport;
  store: LocalStore;
  listener?: SyncListener;
  clock?: Clock;
  /** Окно накопления событий перед применением, мс (§1.3: 100–300). */
  throttleMs?: number;
  /** Задержка перед переподключением, мс; растёт до `maxBackoffMs`. */
  backoffMs?: number;
  maxBackoffMs?: number;
  /** Свой адрес: по нему видно, чьё сообщение пришло — своё не считается непрочитанным. */
  me?: () => string;
}

/** Синхронизация одного узла с локальной базой. */
export class SyncEngine {
  readonly #transport: Transport;
  readonly #store: LocalStore;
  readonly #me: () => string;
  readonly #listener: SyncListener;
  readonly #clock: Clock;
  readonly #throttleMs: number;
  readonly #backoffMs: number;
  readonly #maxBackoffMs: number;
  #abort: AbortController | undefined;
  #running = false;
  /** Чаты, о которых узнали из сообщения раньше, чем из события о самом чате. */
  readonly #missingChats = new Set<string>();

  constructor(options: SyncOptions) {
    this.#transport = options.transport;
    this.#store = options.store;
    this.#listener = options.listener ?? {};
    this.#clock = options.clock ?? systemClock;
    this.#throttleMs = options.throttleMs ?? 150;
    this.#backoffMs = options.backoffMs ?? 500;
    this.#maxBackoffMs = options.maxBackoffMs ?? 30_000;
    this.#me = options.me ?? (() => "");
  }

  /** Открывает стрим и держит его открытым, переподключаясь с последним курсором. */
  start(): void {
    if (this.#running) {
      return;
    }
    this.#running = true;
    void this.#loop();
  }

  stop(): void {
    this.#running = false;
    this.#abort?.abort();
  }

  /**
   * Полная перезагрузка: список чатов и последнее сообщение каждого — для превью в списке.
   * Историю чата тянем не здесь, а при его открытии: на пятистах чатах загрузка «по 50 сообщений
   * каждому» — это больше тысячи запросов на старте, и клиент просто не открывается.
   */
  async reload(): Promise<void> {
    const chats = createClient(ChatService, this.#transport);
    this.#store.reset();
    let pageToken = "";
    for (;;) {
      const page = await chats.listChats({ includeArchived: true, cursor: pageToken, limit: 100 });
      this.#store.batch(() => {
        for (const chat of page.chats) {
          this.#store.upsertChat(toLocalChat(chat));
          if (chat.lastMessage !== undefined) {
            // Последнее сообщение приходит вместе со списком — отдельный запрос за ним не нужен.
            this.#store.upsertMessage(toLocalMessage(chat.lastMessage));
          }
        }
      });
      if (page.page === undefined || !page.page.hasMore || page.page.nextCursor === "") {
        break;
      }
      pageToken = page.page.nextCursor;
    }
    this.#listener.resynced?.();
    this.#listener.changed?.();
  }

  /** История чата: спрашиваем у узла, когда чат открывают, и только если локально её ещё нет. */
  async history(chatId: string, limit = 50): Promise<void> {
    if (this.#store.messages(chatId, { limit: 2 }).length >= 2) {
      return;
    }
    const messages = createClient(MessageService, this.#transport);
    const page = await messages.getMessages({ chatId, limit });
    this.#store.batch(() => {
      for (const m of page.messages) {
        this.#store.upsertMessage(toLocalMessage(m));
      }
    });
    this.#listener.changed?.();
  }

  async #loop(): Promise<void> {
    let backoff = this.#backoffMs;
    while (this.#running) {
      const abort = new AbortController();
      this.#abort = abort;
      try {
        await this.#subscribe(abort.signal);
        backoff = this.#backoffMs;
      } catch (err) {
        if (!this.#running) {
          return;
        }
        this.#listener.disconnected?.(err, backoff);
        await sleep(backoff);
        backoff = Math.min(backoff * 2, this.#maxBackoffMs);
      }
    }
  }

  async #subscribe(signal: AbortSignal): Promise<void> {
    const events = createClient(EventService, this.#transport);
    // Курсор берём из базы: он лежит рядом с данными, которые под него подогнаны.
    const stream = events.subscribe({ cursor: this.#store.cursor }, { signal });

    let batch: Event[] = [];
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Применения выстраиваем в цепочку: порции обязаны ложиться в базу в том же порядке, в каком
    // пришли, иначе курсор уедет вперёд раньше данных.
    let chain: Promise<void> = Promise.resolve();
    let resynced = false;

    const flush = (): void => {
      timer = undefined;
      if (batch.length === 0) {
        return;
      }
      const ready = batch;
      batch = [];
      chain = chain.then(async () => {
        if (await this.#apply(ready)) {
          resynced = true;
        }
      });
    };

    try {
      for await (const response of stream) {
        // В стриме едет конверт: событие может отсутствовать, если узел прислал пустой ответ.
        const event = response.event;
        if (event === undefined) {
          continue;
        }
        batch.push(event);
        // Окно накопления отмеряет таймер, а не приход следующего события: одиночное сообщение
        // иначе лежало бы в буфере до следующего — то есть до пульса, десятки секунд.
        if (batch.length >= 100) {
          if (timer !== undefined) {
            clearTimeout(timer);
          }
          flush();
        } else {
          timer ??= setTimeout(flush, this.#throttleMs);
        }
        await chain;
        if (resynced) {
          return; // перезагрузились: стрим переоткроется с пустым курсором
        }
      }
    } finally {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      flush();
      await chain;
    }
  }

  /**
   * Применяет порцию событий. Возвращает true, если потребовалась полная перезагрузка.
   *
   * Порция режется на куски и между кусками отпускает поток: всплеск в сотню сообщений иначе
   * складывается в одну задачу на десятки миллисекунд, а планка §5 — не занимать поток дольше кадра.
   * Каждый кусок пишется одной транзакцией: так дешевле и так он не остаётся применённым наполовину.
   */
  async #apply(events: readonly Event[]): Promise<boolean> {
    for (let i = 0; i < events.length; i += applyChunk) {
      const done = await this.#applyChunk(events.slice(i, i + applyChunk));
      if (done) {
        return true;
      }
      if (i + applyChunk < events.length) {
        await yieldToLoop();
      }
    }
    return false;
  }

  async #applyChunk(events: readonly Event[]): Promise<boolean> {
    let changed = false;
    let cursor = "";
    let resync = false;
    for (const event of events) {
      if (event.payload.case === "resyncRequired") {
        resync = true;
        break;
      }
    }
    if (resync) {
      await this.reload();
      return true;
    }
    this.#store.batch(() => {
      for (const event of events) {
        if (event.payload.case !== undefined && liveOnly.has(event.payload.case)) {
          // Живое событие курсор не двигает. У пульса он и так пуст, а вот «печатает» и присутствие
          // курсор несут, хотя в журнал не пишутся: сохранив его, клиент после разрыва попросит догон
          // от позиции, которой в журнале нет.
          changed = this.#applyOne(event) || changed;
          continue;
        }
        changed = this.#applyOne(event) || changed;
        if (event.cursor !== "") {
          cursor = event.cursor;
        }
      }
      // Курсор сохраняем ПОСЛЕ применения и в той же транзакции: иначе разрыв между записью курсора
      // и записью данных потеряет события, а на курсор мы больше никогда не вернёмся.
      if (cursor !== "") {
        this.#store.cursor = cursor;
      }
    });
    if (await this.#fetchMissingChats()) {
      changed = true;
    }
    if (changed) {
      this.#listener.changed?.();
    }
    return false;
  }

  /**
   * Сообщение может прийти раньше, чем чат, которому оно принадлежит: когда человека добавляют в группу,
   * узел присылает системное сообщение прежде `chatUpdated` о самом чате. Порядок видов событий контракт
   * не обещает, а в базе на сообщении висит внешний ключ — без этой заглушки падала вся порция вместе
   * с событием о самом чате, и человек оставался с пустым списком. Так было в обоих клиентах: внешние
   * ключи включены и в sql.js, и в нативной базе; путь просто никто не проходил, потому что в тестах
   * группу всегда создавали до входа второго участника.
   *
   * Заглушка живёт миллисекунды: следом идёт `chatUpdated`, а если он почему-то не пришёл — чат
   * дочитывается у узла сразу после порции.
   */
  #ensureChat(chatId: string): void {
    if (chatId === "" || this.#store.chat(chatId) !== undefined) {
      return;
    }
    this.#store.upsertChat({
      id: chatId,
      kind: 0,
      title: "",
      updatedAt: Date.now(),
      lastMessageId: undefined,
      unread: 0,
      archived: false,
      muted: false,
      pinned: false,
      memberCount: 0,
    });
    this.#missingChats.add(chatId);
  }

  /** Дочитывает чаты, о которых узнали из сообщения, а не из события о самом чате. */
  async #fetchMissingChats(): Promise<boolean> {
    if (this.#missingChats.size === 0) {
      return false;
    }
    const ids = [...this.#missingChats];
    this.#missingChats.clear();
    const chats = createClient(ChatService, this.#transport);
    let changed = false;
    for (const id of ids) {
      if (this.#store.chat(id)?.title !== "") {
        continue; // `chatUpdated` уже пришёл следом — спрашивать незачем
      }
      try {
        const answer = await chats.getChat({ chatId: id });
        if (answer.chat !== undefined) {
          this.#store.upsertChat(toLocalChat(answer.chat));
          changed = true;
        }
      } catch {
        // Не вышло — чат останется без названия до следующего события о нём или до перезагрузки.
        // Пустая строка в списке лучше, чем потерянное сообщение: сообщение уже сохранено.
      }
    }
    return changed;
  }

  #applyOne(event: Event): boolean {
    switch (event.payload.case) {
      case "messageNew":
      case "messageEdited": {
        const message = event.payload.value.message;
        if (message === undefined) {
          return false;
        }
        const local = toLocalMessage(message);
        this.#ensureChat(local.chatId);
        const known = this.#store.message(local.id) !== undefined;
        // Своё сообщение могло прийти раньше ответа на отправку: подменяем черновик, а не добавляем.
        if (local.clientMsgId !== "" && !known) {
          this.#store.replacePending(local.clientMsgId, local);
        } else {
          this.#store.upsertMessage(local);
        }
        // Счётчик непрочитанного ведём сами: в живом событии описания чата нет, а значит нет и
        // числа от сервера. Считаем только новое и чужое; системные события значка не зажигают —
        // так же, как на сервере (иначе «Чат создан» даёт единицу в собственном пустом чате).
        if (
          event.payload.case === "messageNew" && !known && local.sender !== this.#me()
          && local.kind !== 4 && !local.deleted
        ) {
          this.#store.bumpUnread(local.chatId);
        }
        return true;
      }
      case "memberUpdated": {
        // Состав в шапке обязан меняться у оставшихся сразу. Узел присылает описание чата при
        // добавлении, но не при удалении — и без этого «14 участников» оставалось враньём до
        // перезагрузки. Точное число всё равно придёт с описанием и перезапишет счёт.
        const update = event.payload.value;
        const address = update.member?.user?.address ?? "";
        if (update.chatId === "") {
          return false;
        }
        this.#ensureChat(update.chatId);
        // Добавлен или вошёл / удалён или вышел; смена роли состав не меняет.
        if (update.change === MemberChange.ADDED) {
          this.#store.changeMembers(update.chatId, 1);
        } else if (update.change === MemberChange.REMOVED) {
          this.#store.changeMembers(update.chatId, -1);
          if (address !== "") {
            this.#store.removeMember(update.chatId, address);
          }
        }
        return true;
      }
      case "reactionsUpdated": {
        // Реакция приходит отдельным событием, а не новым сообщением: переписывать всё сообщение
        // ради неё значило бы затереть местное состояние отправки.
        const update = event.payload.value;
        if (this.#store.message(update.messageId) === undefined) {
          return false;
        }
        this.#store.setReactions(
          update.messageId,
          update.reactions.map((r) => ({ emoji: r.emoji, count: r.count, mine: r.mine })),
        );
        return true;
      }
      case "presenceUpdated": {
        const update = event.payload.value;
        const seen = update.presence?.lastSeen;
        this.#listener.presence?.(
          update.address,
          update.presence?.state ?? 0,
          seen === undefined ? 0 : Number(seen.seconds) * 1000 + Math.floor(seen.nanos / 1e6),
        );
        return false;
      }
      case "typingUpdated": {
        const typing = event.payload.value;
        this.#listener.typing?.(
          typing.chatId,
          typing.address,
          typing.until === undefined ? 0 : Number(typing.until.seconds) * 1000 + Math.floor(typing.until.nanos / 1e6),
        );
        return false;
      }
      case "readUpdated": {
        // Отметка прочтения приходит СОБЫТИЕМ и только так: в описании чата её нет. Пока клиент её
        // не применял, отправитель не узнавал, что его сообщение прочитали, — вторая галочка не
        // появлялась вовсе, хотя узел о прочтении сообщал.
        const read = event.payload.value;
        if (read.chatId === "" || read.address === "" || read.lastReadMessageId === "") {
          return false;
        }
        this.#ensureChat(read.chatId);
        this.#store.setRead(read.chatId, read.address, read.lastReadMessageId);
        return true;
      }
      case "messageDeleted": {
        const id = event.payload.value.messageId;
        const existing = this.#store.message(id);
        if (existing === undefined) {
          return false;
        }
        this.#store.upsertMessage({ ...existing, deleted: true, text: "" });
        return true;
      }
      case "unfamiliarSignIn": {
        const signIn = event.payload.value;
        this.#listener.signIn?.(
          signIn.device?.id ?? "",
          signIn.device?.name ?? "",
          signIn.method,
          signIn.at === undefined ? Date.now() : Number(signIn.at.seconds) * 1000,
        );
        return false;
      }
      case "lobbyUpdated": {
        const update = event.payload.value;
        this.#listener.lobby?.(
          update.targetId,
          update.user?.address ?? "",
          update.user?.displayName ?? "",
          update.state,
        );
        return false; // базу не меняет: заявка живёт на узле
      }
      case "chatUpdated": {
        const chat = event.payload.value.chat;
        if (chat === undefined) {
          return false;
        }
        this.#store.upsertChat(toLocalChat(chat));
        return true;
      }
      default:
        // Неизвестный или живой тип события (печатает, присутствие) базу не меняет — курсор всё равно
        // сохраняется выше, иначе клиент будет вечно переспрашивать одно и то же.
        return false;
    }
  }
}

/** По сколько событий применяем за раз, не отпуская поток. */
const applyChunk = 25;

/** Отпускает поток: макрозадача даёт браузеру отрисовать кадр между кусками. */
function yieldToLoop(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** События, которые живут только в стриме: в журнал они не попадают, и догонять по ним нечего. */
const liveOnly = new Set(["heartbeat", "typingUpdated", "presenceUpdated"]);

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Время контракта в миллисекунды — вместе с долями секунды.
 *
 * Секунд мало: четыре чата, обновлённые в одну секунду, получают одинаковое время, и их порядок в
 * списке начинает зависеть от того, как легли идентификаторы. Список чатов от этого переставляется
 * между перезагрузками — нашлось кадрами Р-24, когда два одинаковых прогона дали разный порядок.
 */
function millis(at: { seconds: bigint; nanos: number } | undefined): number {
  return at === undefined ? 0 : Number(at.seconds) * 1000 + Math.floor(at.nanos / 1e6);
}

/** Сообщение контракта → строка локальной базы. */
export function toLocalMessage(message: ProtoMessage): LocalMessage {
  return {
    id: message.id,
    chatId: message.chatId,
    clientMsgId: message.clientMsgId,
    sender: message.senderAddress,
    kind: message.kind,
    text: message.text,
    createdAt: millis(message.createdAt),
    editedAt: message.editedAt === undefined ? undefined : millis(message.editedAt),
    deleted: message.deleted,
    unreadable: message.unreadable,
    ...(message.reactions.length === 0
      ? {}
      : {
          reactions: message.reactions.map((r) => ({ emoji: r.emoji, count: r.count, mine: r.mine })),
        }),
    ...(message.system === undefined
      ? {}
      : {
          system: {
            type: message.system.type,
            actor: message.system.actorAddress,
            subjects: message.system.subjectAddresses,
            value: message.system.value,
          },
        }),
    state: "sent",
    replyToId: message.replyToId === "" ? undefined : message.replyToId,
  };
}

/** Чат контракта → строка локальной базы. */
export function toLocalChat(chat: ProtoChat): LocalChat {
  return {
    id: chat.id,
    kind: chat.type,
    // У личного чата заголовка нет: имя собеседника подставляет клиент (контракт §3, `peer_address`).
    title: chat.title === "" ? chat.peerAddress : chat.title,
    updatedAt: millis(chat.updatedAt),
    lastMessageId: chat.lastMessage?.id,
    unread: Number(chat.unreadCount),
    archived: chat.archived,
    muted: chat.muted,
    pinned: chat.pinned,
    memberCount: chat.memberCount,
  };
}
